import { TZDate } from '@date-fns/tz';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormHelperText,
  Typography,
} from '@mui/material';
import { Fragment, ReactNode, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormatDate } from 'src/components/atoms/format-date';
import { InputField } from 'src/components/atoms/input-field';
import { ReminderHighlight } from 'src/components/atoms/reminder-highlight';
import { Dialog, DialogActions } from 'src/components/molecules/dialog';
import { Select } from 'src/components/molecules/select';
import { fetchBookings } from 'src/features/bookings/api/lib/bookings';
import { bookingsQueryKeys } from 'src/features/bookings/api/query-keys/bookings';
import { useOrganizationDetails } from 'src/features/organization-details/hooks/api/use-organization-details';
import { useUnassignSpaceFromSpaceGroup } from 'src/features/space-groups/api/hooks/use-unassign-space-from-space-group';
import { fetchSpaces } from 'src/features/spaces/api/lib/spaces';
import { spacesKeys } from 'src/features/spaces/api/query-keys/spaces';
import { SpaceType } from 'src/features/spaces/types/space-type';
import { useMeilisearch } from 'src/hooks/meilisearch/use-meilisearch';
import { useUserStore } from 'src/store/user-store';
import { dateFormats } from 'src/utils/date-formats';
import { z } from 'zod';
import { useDeleteSpaceFromSpaceGroupDialogFormValidationSchema } from './schema/use-form-validation-schema';
import { DeleteSpaceFromSpaceGroupDialogSpacesDrawer } from './spaces-drawer';

interface Props extends DialogActions {
  spaceGroupToken: string;
  spaceGroupName: string;
  spaceToken: string;
}

type BookingAction = 'move' | 'cancel' | 'keep' | '';

export const DeleteSpaceFromSpaceGroupDialog = ({
  open,
  onCancel,
  onConfirm,
  spaceGroupToken,
  spaceGroupName,
  spaceToken,
}: Props) => {
  const { directoryToken } = useUserStore();

  const [openDrawer, setOpenDrawer] = useState<boolean>(false);

  const [selectedBookingToken, setSelectedBookingToken] = useState<string>('');

  const [selectedAction, setSelectedAction] = useState<BookingAction>('');

  const { results: bookings } = useMeilisearch({
    fetch: fetchBookings,
    page: 1,
    perPage: 1000,
    queryKey: bookingsQueryKeys.list(directoryToken ?? '', 1, 1000),
    searchState: {
      filter: [[`spaceToken = ${spaceToken}`]],
      q: '',
      sort: [],
    },
    enabled: Boolean(spaceToken),
  });

  const { results: spaces } = useMeilisearch({
    fetch: fetchSpaces,
    queryKey: spacesKeys.list(directoryToken ?? '', 1, 1000),
    page: 1,
    perPage: 1000,
    searchState: {
      filter: [[`spaceGroupToken = ${spaceGroupToken}`]],
      q: '',
      sort: [],
    },
    enabled: open,
  });

  const otherAvailableSpaces = spaces?.hits?.filter(
    space =>
      space.token !== spaceToken &&
      space.type ===
        spaces?.hits.find(({ token }) => token === spaceToken)?.type,
  );

  const { results: bookingsForOtherAvailableSpaces } = useMeilisearch({
    fetch: fetchBookings,
    page: 1,
    perPage: 1000,
    queryKey: bookingsQueryKeys.list(directoryToken ?? '', 1, 1000),
    searchState: {
      filter: [
        otherAvailableSpaces?.map(space => `spaceToken = ${space.token}`) ?? [],
      ],
      q: '',
      sort: [],
    },
    enabled: Boolean(spaceToken) && Boolean(otherAvailableSpaces),
  });

  const { t } = useTranslation(['spaceGroups', 'common']);

  const { data: organizationDetails } = useOrganizationDetails();

  const tenantTimezone = organizationDetails?.timezone;

  const activeOrFutureBookings = bookings?.hits?.filter(
    booking => booking.status === 'BOOKED',
  );

  const formValidationSchema =
    useDeleteSpaceFromSpaceGroupDialogFormValidationSchema(
      activeOrFutureBookings?.length ?? 0,
    );

  type FormValues = z.infer<typeof formValidationSchema>;

  const defaultValues: FormValues = {
    bookingsDecisions: {
      moveBookingDecisions: [],
      cancelBookingDecisions: [],
      keepBookingUnchangedDecisions: [],
    },
    bookingsDecisionsReason: '',
    responsibilityAgreement: false,
  };

  const {
    watch,
    handleSubmit,
    formState: { errors },
    setValue,
    trigger,
    control,
    reset,
  } = useForm<FormValues>({
    defaultValues,
    resolver: zodResolver(formValidationSchema),
    mode: 'onBlur',
  });

  const bookingsDecisions = watch('bookingsDecisions');
  const bookingsDecisionsReason = watch('bookingsDecisionsReason');

  const handleCancel = () => {
    onCancel?.();
    reset(defaultValues);
  };

  const {
    mutate: unassignSpacesFromSpaceGroup,
    isPending: isUnassigningSpacesFromSpaceGroup,
  } = useUnassignSpaceFromSpaceGroup(onConfirm);

  const handleUnassignSpaceFromSpaceGroup = () => {
    if (!bookingsDecisions) {
      return;
    }

    unassignSpacesFromSpaceGroup({
      spaceGroupToken,
      spaceToken,
      bookingsDecisions,
      bookingsDecisionsReason,
    });
  };

  let content: ReactNode = (
    <Typography variant="body2">
      {t('spaceGroups:details.removeFromGroupDialog.content', {
        groupName: spaceGroupName,
      })}
    </Typography>
  );

  const handleOpenSpaceDrawer = (bookingToken: string) => {
    const updatedCancelDecisions =
      bookingsDecisions.cancelBookingDecisions.filter(
        decision => decision.bookingToken !== bookingToken,
      );

    const updatedKeepDecisions =
      bookingsDecisions.keepBookingUnchangedDecisions.filter(
        decision => decision.bookingToken !== bookingToken,
      );

    setValue('bookingsDecisions', {
      ...bookingsDecisions,
      cancelBookingDecisions: updatedCancelDecisions,
      keepBookingUnchangedDecisions: updatedKeepDecisions,
    });

    setOpenDrawer(true);
    setSelectedBookingToken(bookingToken);
  };

  const handleMoveToAnotherSpace = (
    bookingToken: string,
    spaceToken: string,
    checked: boolean,
  ) => {
    // First, create new arrays without this booking token (if it exists in any of them)
    const updatedCancelDecisions =
      bookingsDecisions.cancelBookingDecisions.filter(
        decision => decision.bookingToken !== bookingToken,
      );

    const updatedKeepDecisions =
      bookingsDecisions.keepBookingUnchangedDecisions.filter(
        decision => decision.bookingToken !== bookingToken,
      );

    let updatedMoveDecisions = [
      ...bookingsDecisions.moveBookingDecisions.filter(
        decision => decision.bookingToken !== bookingToken,
      ),
      { bookingToken, spaceToken },
    ];

    if (!checked) {
      updatedMoveDecisions = updatedMoveDecisions.filter(
        decision => decision.bookingToken !== bookingToken,
      );

      setSelectedBookingToken('');
      setSelectedAction('');
    }

    // Update the form with the new decisions
    setValue('bookingsDecisions', {
      moveBookingDecisions: updatedMoveDecisions,
      cancelBookingDecisions: updatedCancelDecisions,
      keepBookingUnchangedDecisions: updatedKeepDecisions,
    });

    setOpenDrawer(false);
  };

  const handleCancelBooking = (bookingToken: string) => {
    // First, create new arrays without this booking token (if it exists in any of them)
    const updatedMoveDecisions = bookingsDecisions.moveBookingDecisions.filter(
      decision => decision.bookingToken !== bookingToken,
    );

    const updatedKeepDecisions =
      bookingsDecisions.keepBookingUnchangedDecisions.filter(
        decision => decision.bookingToken !== bookingToken,
      );

    const updatedCancelDecisions = [
      ...bookingsDecisions.cancelBookingDecisions.filter(
        decision => decision.bookingToken !== bookingToken,
      ),
      { bookingToken },
    ];

    // Update the form with the new decisions
    setValue('bookingsDecisions', {
      moveBookingDecisions: updatedMoveDecisions,
      cancelBookingDecisions: updatedCancelDecisions,
      keepBookingUnchangedDecisions: updatedKeepDecisions,
    });
  };

  const handleKeepBooking = (bookingToken: string) => {
    // First, create new arrays without this booking token (if it exists in any of them)
    const updatedMoveDecisions = bookingsDecisions.moveBookingDecisions.filter(
      decision => decision.bookingToken !== bookingToken,
    );

    const updatedCancelDecisions =
      bookingsDecisions.cancelBookingDecisions.filter(
        decision => decision.bookingToken !== bookingToken,
      );

    const updatedKeepDecisions = [
      ...bookingsDecisions.keepBookingUnchangedDecisions.filter(
        decision => decision.bookingToken !== bookingToken,
      ),
      { bookingToken },
    ];

    // Update the form with the new decisions
    setValue('bookingsDecisions', {
      moveBookingDecisions: updatedMoveDecisions,
      cancelBookingDecisions: updatedCancelDecisions,
      keepBookingUnchangedDecisions: updatedKeepDecisions,
    });
  };

  const handleChangeBookingAction = (
    action: BookingAction,
    bookingToken: string,
  ) => {
    switch (action) {
      case 'move':
        handleOpenSpaceDrawer(bookingToken);
        break;
      case 'cancel':
        handleCancelBooking(bookingToken);
        break;
      case 'keep':
        handleKeepBooking(bookingToken);
        break;
    }

    setSelectedAction(action);

    trigger('bookingsDecisions');
  };

  const handleCancelSelectedAction = () => {
    setSelectedBookingToken('');
    setSelectedAction('');
    setOpenDrawer(false);
  };

  const getBookingActions: (
    from: number,
    to: number,
    spaceType: SpaceType,
  ) => {
    label: string;
    value: BookingAction;
    disabled?: boolean;
  }[] = (from, to, spaceType) => [
    {
      label: t('spaceGroups:details.removeFromGroupDialog.moveToAnotherSpace'),
      value: 'move',
      disabled:
        bookingsForOtherAvailableSpaces?.hits?.filter(
          booking =>
            booking.spaceType === spaceType &&
            ((booking.from <= from && booking.to >= from) ||
              (booking.from >= from && booking.to <= to) ||
              (booking.from <= to && booking.to >= to)) &&
            booking.status === 'BOOKED',
        ).length !== 0,
    },
    {
      label: t('spaceGroups:details.removeFromGroupDialog.cancelBooking'),
      value: 'cancel',
    },
    {
      label: t('spaceGroups:details.removeFromGroupDialog.keepBooking'),
      value: 'keep',
    },
  ];

  const renderChosenSpace = (bookingToken: string) => {
    const chosenSpaceToken = bookingsDecisions.moveBookingDecisions.find(
      decision => decision.bookingToken === bookingToken,
    )?.spaceToken;

    if (!chosenSpaceToken) {
      return null;
    }

    return (
      <Typography variant="body2" color="text.secondary">
        {
          otherAvailableSpaces?.find(space => space.token === chosenSpaceToken)
            ?.name
        }
      </Typography>
    );
  };

  if (activeOrFutureBookings?.length) {
    content = (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <ReminderHighlight>
          <Typography variant="body2">
            {t('spaceGroups:details.removeFromGroupDialog.hint')}
          </Typography>
        </ReminderHighlight>

        <Box sx={{ overflowY: 'auto', maxHeight: 300 }}>
          {activeOrFutureBookings.map((booking, index) => (
            <Fragment key={booking.token}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 2,
                }}>
                <Box>
                  <Typography variant="body2">
                    {booking.employeeBookerEmail}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    <FormatDate
                      date={new TZDate(booking.from, tenantTimezone)}
                      format={`${dateFormats.day} ${dateFormats.monthShort} ${dateFormats.yearShort}, ${dateFormats.hours}:${dateFormats.minutes}`}
                    />
                    {' - '}
                    <FormatDate
                      date={new TZDate(booking.to, tenantTimezone)}
                      format={`${dateFormats.day} ${dateFormats.monthShort} ${dateFormats.yearShort}, ${dateFormats.hours}:${dateFormats.minutes}`}
                    />
                  </Typography>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Select
                    label={t(
                      'spaceGroups:details.removeFromGroupDialog.selectAction',
                    )}
                    value={selectedAction}
                    items={getBookingActions(
                      booking.from,
                      booking.to,
                      booking.spaceType,
                    )}
                    onChange={event =>
                      handleChangeBookingAction(
                        event.target.value as BookingAction,
                        booking.token,
                      )
                    }
                  />

                  {renderChosenSpace(booking.token) ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}>
                      {renderChosenSpace(booking.token)}

                      <Button
                        sx={{
                          textDecoration: 'underline',
                          ':hover': {
                            textDecoration: 'underline',
                          },
                        }}
                        onClick={() => setOpenDrawer(true)}>
                        {t('common:button.change')}
                      </Button>
                    </Box>
                  ) : null}
                </Box>
              </Box>

              {index < activeOrFutureBookings.length - 1 ? <Divider /> : null}
            </Fragment>
          ))}
        </Box>

        {errors.bookingsDecisions ? (
          <FormHelperText error>
            {errors.bookingsDecisions.message}
          </FormHelperText>
        ) : null}

        <Controller
          control={control}
          name="bookingsDecisionsReason"
          render={({
            field: { name, value, onChange, onBlur },
            fieldState: { error, invalid },
          }) => (
            <InputField
              label={t('details.removeFromGroupDialog.reasonInput.label')}
              name={name}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              error={invalid}
              helperText={
                error?.message ||
                t(
                  'spaceGroups:details.removeFromGroupDialog.reasonInput.helperText',
                )
              }
              required
            />
          )}
        />

        <Controller
          control={control}
          name="responsibilityAgreement"
          render={({
            field: { name, value, onChange, onBlur },
            fieldState: { error, invalid },
          }) => (
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    name={name}
                    checked={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    color="primary"
                  />
                }
                label={t(
                  'spaceGroups:details.removeFromGroupDialog.responsibilityAgreementDescription',
                )}
              />

              {invalid ? (
                <FormHelperText error>{error?.message}</FormHelperText>
              ) : null}
            </Box>
          )}
        />
      </Box>
    );
  }

  return (
    <>
      <Dialog
        variant="warning"
        open={open}
        title={t('spaceGroups:details.removeFromGroupDialog.title')}
        onCancel={handleCancel}
        onConfirm={handleSubmit(handleUnassignSpaceFromSpaceGroup)}
        isConfirmButtonSubmitting={isUnassigningSpacesFromSpaceGroup}
        cancelButtonText={t('common:button.no')}
        confirmButtonId="delete_space_confirm_button"
        confirmButtonText={t(
          'spaceGroups:details.removeFromGroupDialog.confirmButtonText',
        )}>
        {content}
      </Dialog>

      <DeleteSpaceFromSpaceGroupDialogSpacesDrawer
        open={openDrawer}
        selectedBookingToken={selectedBookingToken}
        otherAvailableSpaces={otherAvailableSpaces ?? []}
        onSelectSpace={handleMoveToAnotherSpace}
        onCancelSelectedAction={handleCancelSelectedAction}
        onClose={() => setOpenDrawer(false)}
        bookingSpaceReplacementToken={
          bookingsDecisions.moveBookingDecisions.find(
            decision => decision.bookingToken === selectedBookingToken,
          )?.spaceToken ?? ''
        }
      />
    </>
  );
};
